/**
 * TransactionPreview Component
 *
 * Displays matching transactions for a regex pattern with debounced testing.
 * Shows loading state, match count, highlighted descriptions, and allows
 * users to use a transaction description as an example.
 *
 * @see docs/mockups/regex-builder-final-mockup.html - Design reference
 * @see docs/plans/iterations/regex-builder/contracts/frontend-rails-api.json - API contract
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import clsx from 'clsx';
import { Loader2, ArrowUp } from 'lucide-react';
import { escapeRegex } from './RegexPatternBuilder';

// =============================================================================
// Types
// =============================================================================

export interface MatchingTransaction {
  id: number;
  description: string;
  date: string;
  amount: number;
  direction: 'incoming' | 'outgoing';
}

export interface TestResult {
  matchingTransactions: MatchingTransaction[];
  matchCount: number;
}

export interface TransactionPreviewProps {
  /** Current regex pattern to test */
  pattern: string;
  /** Direction filter for transactions */
  direction: 'all' | 'incoming' | 'outgoing';
  /** Callback to test pattern against database */
  onTestPattern: (pattern: string, direction?: string) => Promise<TestResult>;
  /** Callback when user wants to use a transaction as example */
  onUseAsExample?: (description: string) => void;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Highlight matching terms in a description based on the pattern.
 * Extracts terms from lookahead pattern and highlights them.
 */
function highlightMatches(description: string, pattern: string): React.ReactNode[] {
  if (!pattern || !description) {
    return [description];
  }

  // Extract terms from lookahead patterns (?=.*term)
  const termMatches = pattern.matchAll(/\(\?=\.\*([^)]+)\)/g);
  const terms: string[] = [];
  for (const match of termMatches) {
    // Unescape the term for matching
    const unescaped = match[1].replace(/\\(.)/g, '$1');
    terms.push(unescaped);
  }

  if (terms.length === 0) {
    return [description];
  }

  // Build a regex to find all terms (case-insensitive)
  try {
    const escapedTerms = terms.map(t => escapeRegex(t)).join('|');
    const regex = new RegExp(`(${escapedTerms})`, 'gi');

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;
    let keyIndex = 0;

    while ((match = regex.exec(description)) !== null) {
      // Add text before match
      if (match.index > lastIndex) {
        parts.push(description.slice(lastIndex, match.index));
      }
      // Add highlighted match
      parts.push(
        <mark
          key={`highlight-${keyIndex++}`}
          className="bg-green-200 rounded px-0.5"
          data-testid="match-highlight"
        >
          {match[0]}
        </mark>
      );
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < description.length) {
      parts.push(description.slice(lastIndex));
    }

    return parts.length > 0 ? parts : [description];
  } catch {
    return [description];
  }
}

/**
 * Truncate description to a reasonable length.
 */
function truncateDescription(description: string, maxLength = 60): string {
  if (description.length <= maxLength) {
    return description;
  }
  return description.slice(0, maxLength) + '...';
}

/**
 * Format amount with currency symbol and proper sign.
 */
function formatAmount(amount: number): string {
  const absAmount = Math.abs(amount);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(absAmount);

  return amount >= 0 ? `+${formatted}` : `-${formatted.replace('-', '')}`;
}

// =============================================================================
// Sub-components
// =============================================================================

interface TransactionItemProps {
  transaction: MatchingTransaction;
  pattern: string;
  onUseAsExample?: (description: string) => void;
}

function TransactionItem({ transaction, pattern, onUseAsExample }: TransactionItemProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const isPositive = transaction.amount >= 0;
  const truncated = truncateDescription(transaction.description);
  const highlightedParts = highlightMatches(truncated, pattern);
  const needsTruncation = transaction.description.length > 60;

  return (
    <div
      className={clsx(
        'group flex items-center gap-3 px-3 py-2 text-sm',
        'border-b border-gray-100 last:border-b-0',
        'hover:bg-gray-50',
        onUseAsExample && 'cursor-pointer hover:bg-blue-50'
      )}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      data-testid="transaction-item"
    >
      {/* Date */}
      <span className="text-gray-500 whitespace-nowrap" data-testid="transaction-date">
        {transaction.date}
      </span>

      {/* Description with highlighting */}
      <span
        className="flex-1 font-mono text-xs text-gray-900 overflow-hidden text-ellipsis whitespace-nowrap relative"
        data-testid="transaction-description"
      >
        {highlightedParts}

        {/* Tooltip for full description */}
        {needsTruncation && showTooltip && (
          <span
            className={clsx(
              'absolute left-0 top-full mt-1 z-10',
              'px-3 py-2 max-w-[400px]',
              'bg-gray-800 text-white text-xs rounded-md shadow-lg',
              'whitespace-normal break-all'
            )}
            data-testid="description-tooltip"
          >
            {transaction.description}
          </span>
        )}
      </span>

      {/* Use as Example button (visible on hover) */}
      {onUseAsExample && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onUseAsExample(transaction.description);
          }}
          className={clsx(
            'hidden group-hover:inline-flex items-center gap-1',
            'px-2 py-1 text-[11px] font-medium',
            'bg-blue-600 text-white rounded',
            'hover:bg-blue-700 whitespace-nowrap',
            'focus:outline-none focus:ring-2 focus:ring-blue-500'
          )}
          data-testid="use-as-example-button"
        >
          <ArrowUp size={10} />
          Use as Example
        </button>
      )}

      {/* Amount */}
      <span
        className={clsx(
          'font-medium whitespace-nowrap',
          isPositive ? 'text-green-600' : 'text-red-600'
        )}
        data-testid="transaction-amount"
      >
        {formatAmount(transaction.amount)}
      </span>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * TransactionPreview Component
 *
 * Tests a pattern against the database and displays matching transactions.
 * Features debounced testing, loading states, and "Use as Example" functionality.
 *
 * @example
 * <TransactionPreview
 *   pattern="(?=.*MAZZINI)"
 *   direction="incoming"
 *   onTestPattern={async (pattern, dir) => api.testPattern(pattern, dir)}
 *   onUseAsExample={(desc) => setExampleText(desc)}
 * />
 */
export function TransactionPreview({
  pattern,
  direction,
  onTestPattern,
  onUseAsExample,
}: TransactionPreviewProps) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Use ref to track latest pattern for debounce
  const latestPatternRef = useRef(pattern);
  const latestDirectionRef = useRef(direction);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Update refs when props change
  useEffect(() => {
    latestPatternRef.current = pattern;
    latestDirectionRef.current = direction;
  }, [pattern, direction]);

  // Test pattern with debouncing
  const testPattern = useCallback(async () => {
    const currentPattern = latestPatternRef.current;
    const currentDirection = latestDirectionRef.current;

    if (!currentPattern) {
      setResult(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const testResult = await onTestPattern(
        currentPattern,
        currentDirection === 'all' ? undefined : currentDirection
      );

      // Only update if the pattern hasn't changed during the request
      if (latestPatternRef.current === currentPattern) {
        setResult(testResult);
      }
    } catch (err) {
      if (latestPatternRef.current === currentPattern) {
        setError(err instanceof Error ? err.message : 'Failed to test pattern');
        setResult(null);
      }
    } finally {
      if (latestPatternRef.current === currentPattern) {
        setLoading(false);
      }
    }
  }, [onTestPattern]);

  // Debounced effect for pattern testing
  useEffect(() => {
    // Clear any pending timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // If no pattern, clear results immediately
    if (!pattern) {
      setResult(null);
      setError(null);
      setLoading(false);
      return;
    }

    // Set loading immediately for better UX
    setLoading(true);

    // Debounce the actual API call
    debounceTimerRef.current = setTimeout(() => {
      testPattern();
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [pattern, direction, testPattern]);

  // Compute display values
  const hasTransactions = result && result.matchingTransactions.length > 0;
  const showingCount = result?.matchingTransactions.length ?? 0;
  const totalCount = result?.matchCount ?? 0;

  // Don't render if no pattern
  if (!pattern) {
    return null;
  }

  return (
    <div
      className="border border-gray-200 rounded-lg overflow-hidden"
      data-testid="transaction-preview"
    >
      {/* Header */}
      <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
        <span className="text-sm font-medium text-gray-700" data-testid="match-count-header">
          {loading ? (
            <span className="flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" data-testid="loading-spinner" />
              Testing pattern...
            </span>
          ) : error ? (
            <span className="text-red-600">Error: {error}</span>
          ) : hasTransactions ? (
            `${totalCount} transaction${totalCount !== 1 ? 's' : ''} match${totalCount === 1 ? 'es' : ''} this pattern`
          ) : (
            'No matches found'
          )}
        </span>
      </div>

      {/* Transaction List */}
      {!loading && hasTransactions && (
        <div className="max-h-[200px] overflow-y-auto" data-testid="transaction-list">
          {result.matchingTransactions.map((transaction) => (
            <TransactionItem
              key={transaction.id}
              transaction={transaction}
              pattern={pattern}
              onUseAsExample={onUseAsExample}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && result && !hasTransactions && (
        <div
          className="px-3 py-6 text-center text-sm text-gray-500"
          data-testid="empty-state"
        >
          No transactions match this pattern
        </div>
      )}

      {/* Footer */}
      {!loading && hasTransactions && showingCount < totalCount && (
        <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
          Showing {showingCount} of {totalCount} matches
          {onUseAsExample && (
            <span className="ml-2 text-gray-400">
              Hover for full description, click to use as example
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default TransactionPreview;
